import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Image,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { sporocilo } from '../api/napaka';
import { podatki } from '../api/odjemalec';
import { denar, kolicina } from '../oblika/oblika';
import { javniApi, telefonUrl } from '../ponudba/javno';
import { TemnoOzadje } from '../postavitev/TemnoOzadje';
import { zetonIzPovezave } from '../registracija/zeton';

type Odlocitve = Record<number, boolean>;

/**
 * Odobritev dodatnih del za stranko (brez prijave, povezava iz e-pošte {@code /odobritev#žeton}): status popravila,
 * fotografije in predlagane postavke z zneskom z DDV; pri vsaki »Odobrim« ali »Zavrnem«, nato »Pošlji odgovor«.
 * Odgovorjenih postavk ni mogoče spremeniti (servis o spremembi obvestite po telefonu).
 */
export function Odobritev() {
  const [zeton] = useState(zetonIzPovezave);
  const [odlocitve, setOdlocitve] = useState<Odlocitve>({});
  const stran = useQuery({
    queryKey: ['odobritev', zeton],
    retry: false,
    queryFn: () => podatki(javniApi.POST('/api/javno/odobritev/podatki', { body: { zeton, odlocitve: [] } })),
  });
  const poslji = useMutation({
    mutationFn: () =>
      podatki(
        javniApi.POST('/api/javno/odobritev/odgovor', {
          body: {
            zeton,
            odlocitve: Object.entries(odlocitve).map(([id, odobreno]) => ({ postavkaId: Number(id), odobreno })),
          },
        }),
      ),
    onSuccess: () => {
      void stran.refetch();
      setOdlocitve({});
    },
  });
  const o = stran.data;
  const odprte = (o?.postavke ?? []).filter((p) => !p.odobritev);
  const vseIzbrane = odprte.length > 0 && odprte.every((p) => odlocitve[p.id!] !== undefined);

  return (
    <TemnoOzadje sirina={640}>
      <Paper radius="lg" p={{ base: 20, sm: 32 }} shadow="xl">
        {stran.isPending ? (
          <Stack align="center" py="lg">
            <Loader aria-label="Nalagam" />
          </Stack>
        ) : !o ? (
          <Alert color="red" variant="light" title="Povezava ni veljavna">
            {sporocilo(stran.error)}
          </Alert>
        ) : (
          <Stack gap="md">
            <div>
              <Text c="dimmed" size="sm">
                {o.podjetje}
              </Text>
              <Title order={1} size="h3">
                Odobritev dodatnih del
              </Title>
              <Text size="sm">
                Nalog {o.stevilka} · {o.vozilo}
              </Text>
              <Group gap="xs" mt={4}>
                <Text size="sm">Status popravila:</Text>
                <Badge variant="light">{o.status}</Badge>
              </Group>
            </div>
            {(o.slike ?? []).length > 0 && (
              <SimpleGrid cols={3} spacing="xs">
                {(o.slike ?? []).map((s, i) => (
                  <Image
                    key={i}
                    src={`data:image/jpeg;base64,${s}`}
                    radius="md"
                    h={90}
                    fit="cover"
                    alt={`Fotografija ${i + 1}`}
                  />
                ))}
              </SimpleGrid>
            )}
            <Stack gap="xs">
              {(o.postavke ?? []).map((p) => {
                const izbira = odlocitve[p.id!];
                return (
                  <Paper key={p.id} withBorder radius="md" p="sm" aria-label={p.naziv}>
                    <Group justify="space-between" gap="xs" wrap="wrap">
                      <div style={{ minWidth: 0 }}>
                        <Text fw={600}>{p.naziv}</Text>
                        <Text size="sm" c="dimmed">
                          {kolicina(p.kolicina)} {p.enota} · {denar(p.znesek)} € z DDV
                        </Text>
                      </div>
                      {p.odobritev ? (
                        <Badge color={p.odobritev === 'ODOBRENO' ? 'green' : 'red'} variant="light" size="lg">
                          {p.odobritev === 'ODOBRENO' ? 'Odobreno' : 'Zavrnjeno'}
                        </Badge>
                      ) : (
                        <Group gap="xs">
                          <Button
                            variant={izbira === true ? 'filled' : 'default'}
                            color="green"
                            leftSection={<IconCheck size={16} />}
                            onClick={() => setOdlocitve((x) => ({ ...x, [p.id!]: true }))}
                            aria-pressed={izbira === true}
                          >
                            Odobrim
                          </Button>
                          <Button
                            variant={izbira === false ? 'filled' : 'default'}
                            color="red"
                            leftSection={<IconX size={16} />}
                            onClick={() => setOdlocitve((x) => ({ ...x, [p.id!]: false }))}
                            aria-pressed={izbira === false}
                          >
                            Zavrnem
                          </Button>
                        </Group>
                      )}
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
            {poslji.error && (
              <Alert color="red" variant="light" role="alert">
                {sporocilo(poslji.error)}
              </Alert>
            )}
            {odprte.length > 0 ? (
              <Button size="md" onClick={() => poslji.mutate()} loading={poslji.isPending} disabled={!vseIzbrane}>
                Pošlji odgovor
              </Button>
            ) : (
              <Alert color="green" variant="light">
                Hvala, vaš odgovor smo prejeli. Ko bo vozilo pripravljeno, vas obvestimo.
              </Alert>
            )}
            {o.telefon && (
              <Text size="sm" c="dimmed">
                Vprašanja? <Anchor href={telefonUrl(o.telefon)}>Pokličite nas ({o.telefon})</Anchor>
              </Text>
            )}
          </Stack>
        )}
      </Paper>
    </TemnoOzadje>
  );
}
